import { EVENT_AUDIENCE_OPTIONS, LABEL_USAGE_OPTIONS } from '../../shared/types';
import type { AnalysisType, ComplianceAnalysisResult, MentionsChecklistResult } from '../../shared/types';
import { esc, inlineMd, mdToHtml } from './emailHtml';
import {
  C, FONT_DISPLAY, FONT_FACES, FONT_SANS, TONES, type Tone,
  icon, scoreRingSvg, toneForType, typologyShapeSvg, verdictStyle, visalyMark,
} from './reportTheme';

/* ════════════════════════════════════════════════════════════════════
   RAPPORT PDF — charte Visaly « Cream & Ink »
   ────────────────────────────────────────────────────────────────────
   Le document est rendu dans une iframe isolée puis capturé bloc par
   bloc (cf. pdfExport.ts). Trois règles gouvernent tout ce fichier :

   1. Chaque section porte `.pdf-block` — c'est l'unité de pagination.
      Une carte n'est jamais coupée entre deux pages.
   2. Tout est en style inline et en hexadécimal : le document n'embarque
      pas Tailwind, et html2canvas 1.x plante sur `oklch()`.
   3. Les cartes flottent sur le crème de la page (le fond est peint par
      jsPDF, pas par le HTML) : elles doivent donc être opaques et
      arrondies, comme les tuiles de l'application.
   ════════════════════════════════════════════════════════════════════ */

/** Largeur de composition. Elle est remise à l'échelle de la colonne A4
 *  par pdfExport ; c'est ce rapport qui fixe la taille du texte imprimé —
 *  ne pas l'élargir sans revoir les tailles de police. */
const W = 680;

/* ── Petites briques ──────────────────────────────────────────────── */

/** Coupe au dernier mot entier : un titre tronqué en plein milieu d'un mot
 *  se lit comme un bug, pas comme un raccourci. */
function truncate(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:.…-]+$/, '') + '…';
}

/** Pilule pleine — l'étiquette de la charte : aplat pastel, texte encre. */
function pill(label: string, bg: string, color = C.ink, size = 11): string {
  return `<span style="display:inline-block;height:24px;line-height:24px;padding:0 12px;border-radius:12px;background:${bg};color:${color};font-size:${size}px;font-weight:600;white-space:nowrap;">${esc(label)}</span>`;
}

/** Cartouche de référence légale — pastille blanche cerclée de la teinte. */
function refPill(ref: string, tone: Tone): string {
  if (!ref) return '';
  return `<div style="margin-top:9px;"><span style="display:inline-block;height:21px;line-height:19px;padding:0 9px;border-radius:11px;background:#ffffff;border:1px solid ${tone.soft};color:${tone.deep};font-size:10px;font-weight:600;letter-spacing:0.02em;white-space:nowrap;">Réf. ${esc(ref)}</span></div>`;
}

/** Puce numérotée — le rond pastel qui ouvre chaque constat. */
function indexDot(n: number, tone: Tone): string {
  return `<span style="display:inline-block;width:22px;height:22px;line-height:22px;border-radius:11px;background:${tone.base};color:${C.ink};font-size:11px;font-weight:700;text-align:center;">${n}</span>`;
}

/**
 * Carte de section — l'aplat dilué de la teinte, un filet de la teinte
 * pleine, et un en-tête « porte-icône + titre + compteur ».
 * Marquée `.pdf-block` : elle ne sera jamais coupée en deux pages.
 */
function section(opts: {
  title: string;
  tone: Tone;
  iconName: string;
  count?: number;
  lede?: string;
  inner: string;
}): string {
  const { title, tone, iconName, count, lede, inner } = opts;
  const counter = count && count > 1
    ? `<span style="display:inline-block;height:20px;line-height:20px;padding:0 9px;border-radius:10px;background:${tone.base};color:${C.ink};font-size:10px;font-weight:700;">${count}</span>`
    : '';
  return `<div class="pdf-block" style="background:${tone.wash};border:1px solid ${tone.base};border-radius:22px;padding:24px 28px 22px;">
    <table style="border-collapse:collapse;width:100%;margin:0 0 ${lede ? 10 : 18}px;"><tr>
      <td style="width:30px;padding:0;vertical-align:middle;"><span style="display:inline-block;width:26px;height:26px;border-radius:9px;background:${tone.base};text-align:center;line-height:26px;">${icon(iconName, C.ink, 14)}</span></td>
      <td style="padding:0 0 0 11px;vertical-align:middle;font-family:${FONT_DISPLAY};font-size:13px;letter-spacing:0.1em;text-transform:uppercase;color:${tone.deep};">${esc(title)}</td>
      <td style="padding:0;vertical-align:middle;text-align:right;">${counter}</td>
    </tr></table>
    ${lede ? `<div style="font-size:11.5px;line-height:1.65;color:${tone.deep};opacity:0.78;margin:0 0 18px;">${lede}</div>` : ''}
    ${inner}
  </div>`;
}

/** Constat standard : puce numérotée, énoncé, explication, référence. */
function finding(n: number, tone: Tone, element: string, explanation: string, ref: string, extra = '', badge = ''): string {
  return `<table style="border-collapse:collapse;width:100%;margin:0 0 16px;"><tr>
    <td style="width:22px;padding:2px 0 0;vertical-align:top;">${indexDot(n, tone)}</td>
    <td style="padding:0 0 0 12px;vertical-align:top;">
      <div style="font-size:13.5px;font-weight:600;color:${C.title};line-height:1.5;margin:0 0 5px;">${inlineMd(element)}${badge}</div>
      <div style="font-size:12.5px;color:${C.muted};line-height:1.75;">${inlineMd(explanation)}</div>
      ${extra}
      ${refPill(ref, tone)}
    </td>
  </tr></table>`;
}

/** Encart de recommandation — accent rose de la marque, en retrait. */
function recommendation(text: string): string {
  const t = TONES.rose;
  return `<div style="margin:10px 0 0;padding:9px 13px;background:${t.wash};border-left:3px solid ${t.base};border-radius:0 10px 10px 0;">
    <span style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${t.deep};">Recommandation</span>
    <div style="font-size:12px;color:${t.deep};line-height:1.7;margin-top:3px;">${inlineMd(text)}</div>
  </div>`;
}

/* ── Sections ─────────────────────────────────────────────────────── */

/** En-tête du rapport : le panneau encre de la charte, décoré de la
 *  silhouette de la typologie analysée. */
function cover(opts: { title: string; label: string; tone: Tone; type?: AnalysisType | string; generatedAt: string }): string {
  const { title, label, tone, type, generatedAt } = opts;
  return `<div class="pdf-block" style="position:relative;overflow:hidden;background:${C.ink};border-radius:26px;padding:28px 32px 32px;color:#ffffff;">
    <div style="position:absolute;right:28px;bottom:22px;width:104px;height:104px;opacity:0.85;line-height:0;">${typologyShapeSvg(type, tone.base, 104)}</div>
    <table style="border-collapse:collapse;width:100%;position:relative;"><tr>
      <td style="width:34px;padding:0;line-height:0;vertical-align:middle;">${visalyMark('#ffffff', 34)}</td>
      <td style="padding:0 0 0 11px;vertical-align:middle;">
        <div style="font-family:${FONT_DISPLAY};font-size:25px;line-height:1;letter-spacing:-0.022em;color:#ffffff;">Visaly</div>
      </td>
      <td style="padding:0;vertical-align:middle;text-align:right;white-space:nowrap;font-size:10.5px;letter-spacing:0.1em;text-transform:uppercase;color:rgba(255,255,255,0.55);">${esc(generatedAt)}</td>
    </tr></table>

    <div style="position:relative;margin-top:30px;">${pill(label, tone.base)}</div>
    <div style="position:relative;font-family:${FONT_DISPLAY};font-size:30px;line-height:1.16;letter-spacing:-0.022em;color:#ffffff;margin-top:16px;max-width:${W - 190}px;">${esc(title)}</div>
    <div style="position:relative;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:rgba(255,255,255,0.42);margin-top:14px;">Pré-scoring réglementaire · Produits de santé</div>
  </div>`;
}

/** Rappel de ce qui a été soumis — un rapport doit porter son objet. */
function subjectCard(result: ComplianceAnalysisResult, tone: Tone): string {
  const input = (result.originalInput ?? '').trim();
  const context = (result.originalContext ?? '').trim();
  const images = result.originalImages?.length ?? 0;
  if (!input && !context && !images) return '';

  const meta: string[] = [];
  if (images) meta.push(`${images} visuel${images > 1 ? 's' : ''} joint${images > 1 ? 's' : ''}`);
  const usage = LABEL_USAGE_OPTIONS.find((o) => o.value === result.originalLabelUsage);
  if (usage) meta.push(`Emplacement : ${usage.label}`);
  const audience = EVENT_AUDIENCE_OPTIONS.find((o) => o.value === result.originalEvent?.audience);
  if (audience) meta.push(`Audience : ${audience.label}`);

  return `<div class="pdf-block" style="background:${C.app};border:1px solid ${C.line};border-radius:22px;padding:22px 26px;">
    <div style="font-family:${FONT_DISPLAY};font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:${C.soft};margin:0 0 12px;">Contenu analysé</div>
    ${input ? `<div style="border-left:3px solid ${tone.base};padding:2px 0 2px 14px;font-size:13px;line-height:1.75;color:${C.body};">${esc(truncate(input, 1200))}</div>` : ''}
    ${context ? `<div style="margin-top:14px;font-size:12px;line-height:1.7;color:${C.soft};"><span style="font-weight:600;color:${C.muted};">Contexte déclaré&nbsp;:</span> ${esc(truncate(context, 600))}</div>` : ''}
    ${meta.length ? `<div style="margin-top:14px;">${meta.map((m) => `<span style="display:inline-block;margin:0 6px 0 0;height:22px;line-height:22px;padding:0 10px;border-radius:11px;background:${C.card};color:${C.muted};font-size:10.5px;font-weight:600;">${esc(m)}</span>`).join('')}</div>` : ''}
  </div>`;
}

/** Bloc de score : l'anneau segmenté, le verdict, puis le trio de juges. */
function scoreCard(result: ComplianceAnalysisResult): string {
  const v = verdictStyle(result);
  const defWins = result.judgeDefenseScore >= result.judgeContradicteurScore;

  const judge = (value: number, label: string, bar: string, dim: boolean) => `
    <td style="width:33.33%;padding:0 6px;vertical-align:top;text-align:center;${dim ? 'opacity:0.45;' : ''}">
      <div style="font-family:${FONT_DISPLAY};font-size:22px;line-height:1;color:${C.ink};">${value}<span style="font-size:11px;color:${C.faint};">/100</span></div>
      <div style="font-size:9.5px;letter-spacing:0.14em;text-transform:uppercase;color:${C.soft};margin-top:6px;">${esc(label)}</div>
      <div style="margin:8px auto 0;width:38px;height:3px;border-radius:2px;background:${bar};"></div>
    </td>`;

  return `<div class="pdf-block" style="background:${C.app};border:1px solid ${C.line};border-radius:26px;padding:28px 30px 24px;">
    <table style="border-collapse:collapse;width:100%;"><tr>
      <td style="width:158px;padding:0;vertical-align:middle;">
        <div style="position:relative;width:152px;height:152px;">
          <div style="position:absolute;left:0;top:0;line-height:0;">${scoreRingSvg(result.score, 152)}</div>
          <div style="position:absolute;left:0;top:46px;width:152px;text-align:center;">
            <div style="font-family:${FONT_DISPLAY};font-size:44px;line-height:1;color:${C.title};">${result.score}</div>
            <div style="font-size:9px;letter-spacing:0.14em;text-transform:uppercase;color:${C.faint};margin-top:5px;">sur 100</div>
          </div>
        </div>
      </td>
      <td style="padding:0 0 0 26px;vertical-align:middle;">
        ${pill(v.label, v.tone.base, C.ink, 13)}
        <div style="font-family:${FONT_DISPLAY};font-size:19px;line-height:1.3;color:${C.title};margin-top:14px;">${esc(v.lede)}</div>
        ${result.summary ? `<div style="font-size:12.5px;line-height:1.8;color:${C.muted};margin-top:10px;">${esc(result.summary)}</div>` : ''}
      </td>
    </tr></table>

    <div style="margin-top:22px;padding-top:18px;border-top:1px solid ${C.line};">
      <table style="border-collapse:collapse;width:100%;"><tr>
        ${judge(result.judgeDefenseScore, 'Juge Défense', TONES.khaki.base, !defWins)}
        ${judge(result.judgeContradicteurScore, 'Juge Contradicteur', TONES.red.base, defWins)}
        ${judge(result.score, 'Arbitre', v.tone.base, false)}
      </tr></table>
      <div style="text-align:center;font-size:10.5px;color:${C.faint};margin-top:14px;line-height:1.6;">Le score final est rendu par l'arbitre — ce n'est pas une moyenne des deux juges.</div>
    </div>
  </div>`;
}

/** Checklist déterministe des mentions R5122-3 (vérification par règles). */
function mentionsCard(checklist: MentionsChecklistResult): string {
  if (!checklist.applicable) return '';
  if (checklist.items.length === 0 && checklist.redFlags.length === 0) return '';
  const tone = TONES.neutral;

  const mark = (present: boolean | null) => {
    if (present === true) return `<span style="display:inline-block;width:20px;height:20px;line-height:20px;border-radius:10px;background:${TONES.khaki.base};text-align:center;">${icon('check', C.ink, 12)}</span>`;
    if (present === false) return `<span style="display:inline-block;width:20px;height:20px;line-height:20px;border-radius:10px;background:${TONES.red.base};text-align:center;">${icon('x', C.ink, 12)}</span>`;
    return `<span style="display:inline-block;width:20px;height:20px;line-height:20px;border-radius:10px;background:${C.card};text-align:center;">${icon('dash', C.faint, 12)}</span>`;
  };

  const items = checklist.items.map((it) => `<table style="border-collapse:collapse;width:100%;margin:0 0 12px;"><tr>
    <td style="width:20px;padding:1px 0 0;vertical-align:top;">${mark(it.present)}</td>
    <td style="padding:0 0 0 11px;vertical-align:top;">
      <div style="font-size:12.5px;color:${C.title};line-height:1.55;">${esc(it.label)}${it.legalBasis ? `<span style="color:${C.faint};font-size:10.5px;margin-left:8px;">${esc(it.legalBasis)}</span>` : ''}</div>
      ${it.detail ? `<div style="font-size:11.5px;color:${C.soft};line-height:1.65;margin-top:3px;">${esc(it.detail)}</div>` : ''}
    </td>
  </tr></table>`).join('');

  const flags = checklist.redFlags.length ? `<div style="margin-top:16px;padding:16px 18px;background:${TONES.red.wash};border-radius:16px;">
    <div style="font-family:${FONT_DISPLAY};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${TONES.red.deep};margin:0 0 12px;">Signaux d'alerte détectés</div>
    ${checklist.redFlags.map((f) => `<div style="margin-bottom:11px;">
      <div style="font-size:12.5px;font-weight:600;color:${C.title};line-height:1.5;">${esc(f.pattern)}</div>
      <div style="font-size:11.5px;color:${C.muted};line-height:1.7;margin-top:2px;">${esc(f.explanation)}</div>
      ${f.excerpt ? `<div style="font-size:11.5px;color:${TONES.red.deep};line-height:1.6;margin-top:3px;font-style:italic;">«&nbsp;${esc(truncate(f.excerpt, 180))}&nbsp;»</div>` : ''}
      ${f.legalBasis ? refPill(f.legalBasis, TONES.red) : ''}
    </div>`).join('')}
  </div>` : '';

  const lede = `${checklist.regime ? `Régime appliqué&nbsp;: ${esc(checklist.regime)}. ` : ''}Vérification par règles, indépendante des juges IA — elle complète le verdict, elle ne le remplace pas.`;

  return section({ title: 'Mentions obligatoires', tone, iconName: 'shield', lede, inner: items + flags });
}

/* ── Générateur ───────────────────────────────────────────────────── */

export interface PdfReportMeta {
  /** Libellé de la typologie (« Accroche / Claim »…), affiché en pilule. */
  analysisLabel?: string;
  /** Typologie — pilote la teinte et la silhouette du panneau d'en-tête. */
  analysisType?: AnalysisType | string;
}

/**
 * Construit le HTML du rapport pour l'export PDF, à la charte Visaly.
 * Chaque section porte `.pdf-block` : pdfExport les capture une par une
 * et les pagine sans jamais couper une carte entre deux pages.
 */
export function buildAnalysisPdfHtml(
  result: ComplianceAnalysisResult,
  generatedAt: string,
  meta: PdfReportMeta = {},
): string {
  const isQuestion = !!result.generalAnswer;
  const tone = toneForType(meta.analysisType ?? (isQuestion ? 'general_question' : null));
  const label = meta.analysisLabel ?? (isQuestion ? 'Question réglementaire' : 'Analyse de conformité');
  const subject = (result.originalInput ?? '').trim();
  const title = subject ? truncate(subject, 96) : label;

  let body = cover({ title, label, tone, type: meta.analysisType ?? (isQuestion ? 'general_question' : undefined), generatedAt });

  // Le rappel de l'objet n'a de sens que s'il apporte plus que le titre.
  if (subject.length > 96 || result.originalContext || result.originalImages?.length) {
    body += subjectCard(result, tone);
  }

  if (isQuestion) {
    body += `<div class="pdf-block" style="background:${C.app};border:1px solid ${C.line};border-radius:22px;padding:26px 30px;font-size:13px;color:${C.body};line-height:1.8;">${mdToHtml(result.generalAnswer!)}</div>`;
    if (result.summary) {
      body += `<div class="pdf-block" style="padding:2px 30px;font-style:italic;color:${C.soft};font-size:12.5px;line-height:1.75;">${mdToHtml(result.summary)}</div>`;
    }
  } else {
    body += scoreCard(result);
  }

  if (!isQuestion && result.outOfScopeFlags?.length) {
    const t = TONES.clay;
    const inner = result.outOfScopeFlags.map((f, i) => finding(
      i + 1, t, f.element, f.explanation, f.legalBasis, '',
      `<span style="display:inline-block;vertical-align:middle;margin-left:8px;height:19px;line-height:19px;padding:0 9px;border-radius:10px;background:${t.base};color:${C.ink};font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">${esc(f.topic)}</span>`,
    )).join('');
    body += section({
      title: 'Hors périmètre publicité santé',
      tone: t,
      iconName: 'flag',
      count: result.outOfScopeFlags.length,
      lede: "Ces points ne relèvent pas de la réglementation de la publicité des produits de santé et n'affectent pas le score. Ils signalent un risque juridique distinct (dépôt de marque, image).",
      inner,
    });
  }

  if (result.interdictionsFormelles?.length) {
    const t = TONES.red;
    const inner = result.interdictionsFormelles.map((it, i) => finding(i + 1, t, it.element, it.explanation, it.legalBasis)).join('');
    body += section({ title: 'Interdictions formelles', tone: t, iconName: 'x', count: result.interdictionsFormelles.length, inner });
  }

  if (result.pointsDeVigilance?.length) {
    const t = TONES.sun;
    const riskTone: Record<string, Tone> = { Faible: TONES.sun, Moyen: TONES.clay, 'Élevé': TONES.red };
    const inner = result.pointsDeVigilance.map((it, i) => {
      const rt = riskTone[it.riskLevel] ?? TONES.sun;
      const badge = `<span style="display:inline-block;vertical-align:middle;margin-left:8px;height:19px;line-height:19px;padding:0 9px;border-radius:10px;background:${rt.base};color:${C.ink};font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">${esc(it.riskLevel)}</span>`;
      return finding(i + 1, t, it.element, it.explanation, it.legalBasis, it.recommendation ? recommendation(it.recommendation) : '', badge);
    }).join('');
    body += section({ title: 'Points de vigilance', tone: t, iconName: 'bolt', count: result.pointsDeVigilance.length, inner });
  }

  if (result.conformePoints?.length) {
    const t = TONES.khaki;
    const inner = result.conformePoints.map((it, i) => finding(i + 1, t, it.element, it.explanation, it.legalBasis)).join('');
    body += section({ title: 'Points conformes', tone: t, iconName: 'check', count: result.conformePoints.length, inner });
  }

  if (!isQuestion && result.mentionsChecklist) {
    body += mentionsCard(result.mentionsChecklist);
  }

  if (result.sources?.length) {
    const t = TONES.sky;
    const typeTone: Record<string, Tone> = {
      LOI: TONES.sky, JURISPRUDENCE: TONES.rose, ARPP: TONES.neutral, DOCTRINE: TONES.neutral,
    };
    // Une seule table pour toutes les sources : les pastilles de type n'ont
    // pas la même largeur, seule une colonne partagée aligne les intitulés.
    const rows = result.sources.map((s) => {
      const st = typeTone[s.type] ?? TONES.neutral;
      return `<tr>
        <td style="padding:0 0 9px;vertical-align:top;white-space:nowrap;"><span style="display:inline-block;height:19px;line-height:19px;padding:0 9px;border-radius:10px;background:${st.base};color:${C.ink};font-size:9.5px;font-weight:700;letter-spacing:0.06em;">${esc(s.type)}</span></td>
        <td style="padding:0 0 9px 12px;vertical-align:top;font-size:12px;color:${C.muted};line-height:1.6;">${esc(s.title)} — ${esc(s.reference)}${s.date ? ` <span style="color:${C.faint};">(${esc(s.date)})</span>` : ''}</td>
      </tr>`;
    }).join('');
    const inner = `<table style="border-collapse:collapse;width:100%;">${rows}</table>`;
    body += section({ title: 'Sources juridiques', tone: t, iconName: 'doc', count: result.sources.length, inner });
  }

  if (!isQuestion && result.arbitrageDetails) {
    body += section({
      title: "Détails de l'arbitrage",
      tone: TONES.neutral,
      iconName: 'scale',
      inner: `<div style="font-size:12.5px;color:${C.muted};line-height:1.8;">${mdToHtml(result.arbitrageDetails)}</div>`,
    });
  }

  // Mentions de bas de rapport — carte crème, pas un pied de page maigre.
  body += `<div class="pdf-block" style="background:${C.card};border-radius:22px;padding:20px 26px;">
    <table style="border-collapse:collapse;width:100%;"><tr>
      <td style="width:26px;padding:0;line-height:0;vertical-align:middle;">${visalyMark(C.ink, 26)}</td>
      <td style="padding:0 0 0 11px;vertical-align:middle;white-space:nowrap;">
        <div style="font-family:${FONT_DISPLAY};font-size:15px;line-height:1.1;color:${C.ink};">Visaly</div>
        <div style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:${C.soft};margin-top:3px;">Studio Blackthorns · visaly.fr</div>
      </td>
      <td style="width:100%;padding:0 0 0 24px;vertical-align:middle;font-size:10.5px;color:${C.soft};line-height:1.7;">
        Aide à la décision, non-opposable&nbsp;: ce pré-scoring ne remplace ni un avis juridique ni la décision de l'ANSM.<br>
        Textes cités depuis Légifrance — DILA, Licence Ouverte 2.0.
      </td>
    </tr></table>
  </div>`;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">
<base href="${origin}/">
<style>
${FONT_FACES}
  *{ -webkit-print-color-adjust:exact; print-color-adjust:exact; box-sizing:border-box; }
  body{ margin:0; background:${C.cream}; }
  #pdf-root{ width:${W}px; color:${C.body}; font-family:${FONT_SANS}; }
  /* Les blocs flottent sur le crème. En capture, l'écart vertical est posé
     par pdfExport (les marges ne sont pas rendues) ; la marge ci-dessous
     sert au repli « boîte d'impression », qui rend le HTML tel quel. */
  .pdf-block + .pdf-block{ margin-top:14px; }
  .pdf-block{ break-inside:avoid; page-break-inside:avoid; }
  @media print{ body{ padding:16px; } }
  strong{ font-weight:600; color:${C.title}; }
  a{ color:${TONES.sky.deep}; text-decoration:none; }
</style>
</head>
<body>
  <div id="pdf-root">${body}</div>
</body></html>`;
}
